import * as React from 'react'
import { Cell, BarChart as RechartsBarChart, ResponsiveContainer } from 'recharts'

import { Bar } from '../../../../components/chart/Bar'
import { CartesianGrid } from '../../../../components/chart/CartesianGrid'
import { ChartContext } from '../../../../components/chart/ChartWrapper'
import { CursorBar } from '../../../../components/chart/Cursor'
import { Tooltip } from '../../../../components/chart/Tooltip'
import { XAxis } from '../../../../components/chart/XAxis'
import { YAxis } from '../../../../components/chart/YAxis'
import { ChartItemColorType } from '../../../../components/chart/constants'
import ChartTooltip from '../../../../components/line-stacked-bar-chart/ChartTooltip'
import { ChangeTypeEnum } from '../../constants'
import { STACKED_BAR_CHART_HEIGHT, STACKED_BAR_CHART_MARGIN, STACKED_BAR_LABELS_WIDTH } from './constants'
import { formatQuarter, tickFormatter } from './utils'

interface Props {
  chartData: Array<{ quarter: string; smallestSector: string | null } & Record<string, number>>
  sectors: Array<{ sector: string; color: string }>
  type: ChangeTypeEnum
}

export default function StackedBarChart({ chartData, sectors, type }: Props) {
  const [reverseTooltipDirection, setReverseTooltipDirection] = React.useState(true)

  return (
    <ChartContext>
      <ResponsiveContainer height={STACKED_BAR_CHART_HEIGHT}>
        <RechartsBarChart
          data={chartData}
          margin={STACKED_BAR_CHART_MARGIN}
          stackOffset="sign" // because we can have negative values
          onMouseMove={(_, mouseEvent) => {
            setReverseTooltipDirection(mouseEvent.clientX > window.innerWidth / 2)
          }}
        >
          <YAxis
            orientation="left"
            textAnchor="end"
            tickFormatter={(value) => tickFormatter(value, type)}
            width={STACKED_BAR_LABELS_WIDTH}
            domain={type === ChangeTypeEnum.Percentage ? [0, 100] : undefined}
          />
          <XAxis dataKey="quarter" type="category" scale="auto" tickFormatter={(value) => formatQuarter(value)} />
          <CartesianGrid />
          <Tooltip
            allowEscapeViewBox={{ x: true }}
            reverseDirection={{ x: reverseTooltipDirection }}
            cursor={<CursorBar numberOfDatapoints={null} />}
            content={<ChartTooltip valueFormatter={(value) => tickFormatter(+value!, type, 2)} isReversed />}
          />
          {sectors.map(({ sector, color }) => (
            <Bar
              key={sector}
              name={sector}
              dataKey={sector}
              stackId="quarter"
              shape={undefined}
              color={color as ChartItemColorType}
              fill={color}
            >
              {chartData.map(({ smallestSector }, index) => (
                <Cell
                  key={index}
                  className="stroke-bg-primary stroke-[0.25px]"
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore - number[] should also be allowed type for radius, but it's not
                  radius={smallestSector === sector ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </Bar>
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChartContext>
  )
}
